import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { apiClient, formatApiErrorDetail } from "../../lib/api";
import { Header } from "../../components/vc/Header";
import { UploadCard } from "../../components/vc/UploadCard";
import { PrimaryButton, SecondaryButton } from "../../components/vc/Buttons";
import { Section } from "../../components/vc/layout";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../../components/ui/select";
import {
  ArrowLeft, FileSpreadsheet, Image as ImageIcon, Pencil, ClipboardList, Calendar, Flag, Trash2, CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";

const PRIORITY_OPTIONS = [
  { v: "low", l: "Low", cls: "bg-slate-100 text-slate-600" },
  { v: "medium", l: "Medium", cls: "bg-sky-100 text-sky-700" },
  { v: "high", l: "High", cls: "bg-amber-100 text-amber-700" },
  { v: "urgent", l: "Urgent", cls: "bg-red-100 text-red-700" },
];

export default function VCAssignTask() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const preselected = params.get("employee_id") || "";

  const [mode, setMode] = useState("manual"); // manual | excel | image
  const [notes, setNotes] = useState("");
  const [title, setTitle] = useState("");
  const [priority, setPriority] = useState("medium");
  const [dueDate, setDueDate] = useState("");
  const [employeeId, setEmployeeId] = useState(preselected);
  const [employees, setEmployees] = useState([]);
  const [imagePreview, setImagePreview] = useState("");
  const [excelRows, setExcelRows] = useState([]);
  const [excelBusy, setExcelBusy] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    apiClient.get("/employees").then((r) => setEmployees(r.data || [])).catch(() => {});
  }, []);

  const onExcel = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setExcelBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await apiClient.post("/import/parse", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const rows = (data.items || []).map((it, i) => ({
        id: i,
        title: it.product || `Task ${i + 1}`,
        details: `${it.quantity ?? ""} ${it.unit ?? ""}`.trim(),
      }));
      if (rows.length === 0) { toast.error("No tasks found in file"); return; }
      setExcelRows(rows);
      setMode("excel");
      toast.success(`${rows.length} tasks ready to assign`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Excel parse failed");
    } finally {
      setExcelBusy(false);
      e.target.value = "";
    }
  };

  const onImage = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) { toast.error("Image too large (max 3MB)"); return; }
    const reader = new FileReader();
    reader.onload = () => { setImagePreview(reader.result); setMode("image"); };
    reader.readAsDataURL(file);
  };

  const removeExcelRow = (id) => setExcelRows((r) => r.filter((row) => row.id !== id));

  const commonPayload = () => ({
    deadline: dueDate || null,
    employee_id: employeeId || null,
    description: buildDescription(),
    priority,
    image_data: mode === "image" ? imagePreview || null : null,
  });

  const buildDescription = () => {
    const bits = [];
    if (notes.trim()) bits.push(notes.trim());
    return bits.join("\n").trim();
  };

  const validate = () => {
    if (!employeeId) { toast.error("Choose an employee"); return false; }
    if (mode === "manual" && !title.trim() && !notes.trim()) {
      toast.error("Add a task title or notes"); return false;
    }
    if (mode === "excel" && excelRows.length === 0) {
      toast.error("No tasks to assign"); return false;
    }
    if (mode === "image" && !imagePreview) {
      toast.error("Upload an image first"); return false;
    }
    return true;
  };

  const assign = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      if (mode === "excel") {
        // Create one task per row (sequential to preserve order; backend has no bulk endpoint)
        for (const row of excelRows) {
          await apiClient.post("/tasks", {
            title: row.title,
            ...commonPayload(),
            description: [row.details, buildDescription()].filter(Boolean).join(" · "),
          });
        }
        toast.success(`Assigned ${excelRows.length} tasks`);
      } else {
        await apiClient.post("/tasks", {
          title: (title.trim() || notes.trim().split("\n")[0] || "Task"),
          ...commonPayload(),
        });
        toast.success("Task assigned");
      }
      // Stay in the new workflow — jump to assignee's detail (or fall back to Employees list)
      navigate(employeeId ? `/employees/${employeeId}` : "/employees");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Header />
      <div className="px-4 pt-4 pb-40 space-y-5">
        <button onClick={() => navigate(-1)} data-testid="assign-task-back" className="flex items-center gap-1 text-sm font-semibold text-slate-600">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        <div>
          <div className="inline-flex items-center gap-1.5 text-emerald-600 text-[10px] font-bold uppercase tracking-widest mb-1">
            <ClipboardList className="w-3 h-3" strokeWidth={2.6} /> New Task
          </div>
          <h2 className="text-2xl font-bold text-slate-900 leading-tight">Assign Task</h2>
          <p className="text-sm text-slate-500 mt-0.5">Delegate work to your team</p>
        </div>

        {/* Method tiles */}
        <div className="space-y-2.5">
          <UploadCard
            title="Upload Excel"
            subtitle="Each row becomes one task"
            Icon={FileSpreadsheet}
            tone="emerald"
            accept=".xlsx,.csv"
            onChange={onExcel}
            testid="assign-method-excel"
          />
          <UploadCard
            title="Upload Image"
            subtitle="Attach a photo brief"
            Icon={ImageIcon}
            tone="indigo"
            accept="image/*"
            onChange={onImage}
            testid="assign-method-image"
          />
          <UploadCard
            title="Manual Notes"
            subtitle="Write out the task"
            Icon={Pencil}
            tone="amber"
            onClick={() => { setMode("manual"); setImagePreview(""); setExcelRows([]); }}
            testid="assign-method-manual"
          />
        </div>

        {excelBusy && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4 text-sm text-emerald-800 font-semibold">
            Parsing your Excel...
          </div>
        )}

        {/* Excel preview */}
        {mode === "excel" && excelRows.length > 0 && (
          <Section title="Tasks from Excel" subtitle={`${excelRows.length} rows — will create ${excelRows.length} tasks`} testid="excel-preview">
            <div className="space-y-2">
              {excelRows.map((row, i) => (
                <div
                  key={row.id}
                  data-testid={`excel-task-${row.id}`}
                  className="flex items-start gap-3 bg-white border border-slate-100 rounded-2xl p-3 shadow-sm"
                >
                  <div className="w-8 h-8 rounded-lg bg-emerald-100 text-emerald-700 grid place-items-center shrink-0 text-xs font-bold">
                    {i + 1}
                  </div>
                  <div className="min-w-0 flex-1">
                    <Input
                      data-testid={`excel-task-title-${row.id}`}
                      value={row.title}
                      onChange={(e) => setExcelRows((r) => r.map((it) => it.id === row.id ? { ...it, title: e.target.value } : it))}
                      className="h-10 rounded-lg text-sm"
                    />
                    {row.details && <div className="text-xs text-slate-500 mt-1">{row.details}</div>}
                  </div>
                  <button
                    onClick={() => removeExcelRow(row.id)}
                    data-testid={`excel-task-delete-${row.id}`}
                    className="w-8 h-8 rounded-lg bg-red-50 hover:bg-red-100 text-red-600 grid place-items-center transition"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          </Section>
        )}

        {/* Image preview */}
        {mode === "image" && imagePreview && (
          <Section title="Attached image" testid="image-preview-section">
            <div className="bg-white rounded-2xl border border-slate-100 p-3 space-y-2">
              <img data-testid="assign-task-image" src={imagePreview} alt="Task" className="w-full rounded-xl object-cover max-h-64" />
              <SecondaryButton
                onClick={() => { setImagePreview(""); setMode("manual"); }}
                data-testid="assign-image-remove"
                className="w-full"
              >
                <Trash2 className="w-4 h-4 inline mr-1" /> Remove image
              </SecondaryButton>
            </div>
          </Section>
        )}

        {/* Manual entry / task fields */}
        {mode !== "excel" && (
          <div className="bg-white rounded-2xl border border-slate-100 p-4 space-y-3 shadow-sm" data-testid="task-form-fields">
            <div className="space-y-1.5">
              <Label>Task title</Label>
              <Input
                data-testid="assign-input-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g., Deliver rice to Sharma Traders"
                className="h-12 rounded-xl text-base"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Notes</Label>
              <Textarea
                data-testid="assign-input-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={4}
                placeholder="Details, address, contact person..."
                className="rounded-xl text-base"
              />
            </div>
          </div>
        )}

        {/* Priority + Due date */}
        <div className="bg-white rounded-2xl border border-slate-100 p-4 space-y-3 shadow-sm">
          <div className="space-y-1.5">
            <Label className="flex items-center gap-1.5">
              <Flag className="w-3.5 h-3.5" /> Priority
            </Label>
            <div className="grid grid-cols-4 gap-1.5" data-testid="assign-priority">
              {PRIORITY_OPTIONS.map((p) => (
                <button
                  key={p.v}
                  onClick={() => setPriority(p.v)}
                  data-testid={`assign-priority-${p.v}`}
                  className={`h-10 rounded-xl text-xs font-bold transition ${
                    priority === p.v ? p.cls + " ring-2 ring-offset-1 ring-slate-900/10" : "bg-slate-50 text-slate-500 hover:bg-slate-100"
                  }`}
                >
                  {p.l}
                </button>
              ))}
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="flex items-center gap-1.5">
              <Calendar className="w-3.5 h-3.5" /> Due date
            </Label>
            <Input
              data-testid="assign-due-date"
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              className="h-12 rounded-xl text-base"
            />
          </div>
          <div className="space-y-1.5">
            <Label>Assign to employee *</Label>
            {employees.length === 0 ? (
              <SecondaryButton onClick={() => navigate("/employees")} className="w-full">
                Add an employee first
              </SecondaryButton>
            ) : (
              <Select value={employeeId || "none"} onValueChange={(v) => setEmployeeId(v === "none" ? "" : v)}>
                <SelectTrigger data-testid="assign-employee-select" className="h-12 rounded-xl">
                  <SelectValue placeholder="Choose employee" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Choose employee —</SelectItem>
                  {employees.map((e) => (
                    <SelectItem key={e.id} value={e.id}>{e.name}{e.role ? ` · ${e.role}` : ""}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        </div>

        <PrimaryButton
          onClick={assign}
          loading={submitting}
          data-testid="assign-task-submit"
          className="w-full h-14 flex items-center justify-center gap-2 text-base"
        >
          <CheckCircle2 className="w-5 h-5" />
          {mode === "excel" ? `Assign ${excelRows.length} Task${excelRows.length === 1 ? "" : "s"}` : "Assign Task"}
        </PrimaryButton>
      </div>
    </>
  );
}
